import { useEffect, useState } from "react";
import { api, idr, idrShort } from "../../lib/api";
import { toast } from "sonner";
import { Building, Plus, TrendingDown, Calendar, MapPin, Play, X, Trash2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import InputRupiah from "../../components/InputRupiah";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { hariIniISO } from "../../lib/tanggal";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";

// `/finance/aset-tetap` mengurut created_at desc lalu memotong
// `to_list(1000)` di backend TANPA parameter urut dari klien (lihat
// aset_tetap.py) — sort di sini murni client-side, wajib didampingi
// `BatasDaftarNotice`.
const ASET_BATAS_SERVER = 1000;

const KATEGORI_ASET = [
  { v: "elektronik", l: "Elektronik", c: "sky" },
  { v: "furniture",  l: "Furniture",  c: "amber" },
  { v: "kendaraan",  l: "Kendaraan",  c: "emerald" },
  { v: "renovasi",   l: "Renovasi",   c: "purple" },
];

export default function FinanceAsetTetap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [kasBank, setKasBank] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    setLoadError(null);
    api.get("/finance/aset-tetap")
      .then(r => setData(r.data))
      .catch((e) => { setLoadError(e); toast.error("Gagal load"); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); api.get("/finance/accounts/kas-bank").then(r => setKasBank(r.data || [])); }, []);

  const trigger = async () => {
    if (!confirm("Jalankan depresiasi manual untuk bulan ini? (Biasanya otomatis tiap tanggal 1)")) return;
    setBusy(true);
    try { const r = await api.post("/finance/aset-tetap/trigger-depresiasi", {}); toast.success(`Depresiasi ${r.data.count} aset · Total ${idr(r.data.total || 0)}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  // Urutan bawaan (created_at desc dari backend) tetap sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = sortRows(data?.items || [], sortKey, sortDir, {
    nama: (a) => a.nama || "",
    kategori: (a) => a.kategori || "",
    lokasi: (a) => a.lokasi_cabang || "",
    perolehan: (a) => a.nilai_perolehan || 0,
    buku: (a) => a.nilai_buku || 0,
    umur: (a) => a.umur_bulan || 0,
  });

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-aset-tetap">
      <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={3} data-testid="loading-aset-tetap">
      {data && <>
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Fixed Asset Register</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Aset Tetap</h1>
          <p className="text-sm text-slate-600 mt-1">{data.count} aset · Perolehan {idrShort(data.total_perolehan)} · Nilai Buku {idrShort(data.total_buku)}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={trigger} disabled={busy} data-testid="aset-run-depresiasi" className="h-10 px-4 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-2 disabled:opacity-50"><Play className="w-4 h-4" /> Jalankan Depresiasi</button>
          <button onClick={() => setShowForm(true)} data-testid="aset-new" className="h-10 px-4 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-medium inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Aset Baru</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Nilai Perolehan</p>
          <p className="font-display text-2xl font-bold text-slate-900 mt-2">{idr(data.total_perolehan)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Nilai Buku Neto</p>
          <p className="font-display text-2xl font-bold text-indigo-700 mt-2">{idr(data.total_buku)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Akumulasi Penyusutan</p>
          <p className="font-display text-2xl font-bold text-rose-700 mt-2">{idr(data.total_akum)}</p>
        </div>
      </div>

      <BatasDaftarNotice shown={data.items.length} batas={ASET_BATAS_SERVER} />
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-[11px] uppercase text-slate-500"><tr>
              <SortTh label="Kode / Nama" sortKeyName="nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="aset-sort-nama" />
              <SortTh label="Kategori" sortKeyName="kategori" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="aset-sort-kategori" />
              <SortTh label="Lokasi / PIC" sortKeyName="lokasi" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="aset-sort-lokasi" />
              <SortTh label="Perolehan" sortKeyName="perolehan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="aset-sort-perolehan" />
              <SortTh label="Nilai Buku" sortKeyName="buku" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="aset-sort-buku" />
              <SortTh label="Umur (bulan)" sortKeyName="umur" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="aset-sort-umur" />
              <th className="px-4 py-2.5 text-center">Status</th>
              <th className="px-4 py-2.5 text-right">Detail</th>
            </tr></thead>
            <tbody>
              {data.items.length === 0 ? <tr><td colSpan={8} className="p-10 text-center text-slate-400">Belum ada aset</td></tr> :
                sortedItems.map(a => {
                  const kat = KATEGORI_ASET.find(k => k.v === a.kategori);
                  const susutPct = a.nilai_perolehan > 0 ? Math.round(a.akumulasi_penyusutan / a.nilai_perolehan * 100) : 0;
                  return (
                    <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3"><p className="font-mono text-xs text-slate-500">{a.code}</p><p className="font-medium">{a.nama}</p></td>
                      <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-${kat?.c || "slate"}-50 text-${kat?.c || "slate"}-700 border-${kat?.c || "slate"}-200`}>{kat?.l || a.kategori}</span></td>
                      <td className="px-4 py-3 text-xs text-slate-600"><p>{a.lokasi_cabang || "-"}</p><p className="text-[10px] text-slate-400">{a.pic || ""}</p></td>
                      <td className="px-4 py-3 text-right font-mono">{idr(a.nilai_perolehan)}</td>
                      <td className="px-4 py-3 text-right"><p className="font-mono font-semibold text-indigo-700">{idr(a.nilai_buku)}</p><p className="text-[10px] text-slate-400">Susut {susutPct}%</p></td>
                      <td className="px-4 py-3 text-right text-slate-600">{a.umur_bulan}</td>
                      <td className="px-4 py-3 text-center"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${a.status === "aktif" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{a.status}</span></td>
                      <td className="px-4 py-3 text-right"><button onClick={() => setShowDetail(a.id)} className="text-xs text-indigo-700 hover:underline">Lihat</button></td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <FormAset kasBank={kasBank} onClose={() => { setShowForm(false); load(); }} />}
      {showDetail && <DetailAset id={showDetail} onClose={() => setShowDetail(null)} />}
      </>}
      </LoadingState>
    </div>
  );
}

function FormAset({ kasBank, onClose }) {
  const [f, setF] = useState({ nama: "", kategori: "elektronik", tanggal_beli: hariIniISO(), nilai_perolehan: "", umur_bulan: 36, lokasi_cabang: "", pic: "", brand: "", sumber_akun_id: "", catatan: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { const r = await api.post("/finance/aset-tetap", { ...f, nilai_perolehan: Number(f.nilai_perolehan), umur_bulan: Number(f.umur_bulan) }); toast.success(r.data.message); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  const susutPerBulan = f.nilai_perolehan && f.umur_bulan ? Number(f.nilai_perolehan) / Number(f.umur_bulan) : 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-4">Daftar Aset Tetap Baru</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nama" col2><input value={f.nama} onChange={e => setF({ ...f, nama: e.target.value })} className="in" data-testid="aset-nama" /></Field>
          <Field label="Kategori"><select value={f.kategori} onChange={e => setF({ ...f, kategori: e.target.value })} className="in">{KATEGORI_ASET.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}</select></Field>
          <Field label="Tanggal Beli"><DatePicker value={f.tanggal_beli} onChange={v => setF({ ...f, tanggal_beli: v })} data-testid="aset-tanggal-beli" /></Field>
          <Field label="Nilai Perolehan"><InputRupiah value={f.nilai_perolehan} onChange={n => setF({ ...f, nilai_perolehan: n })} className="in" testid="aset-nilai" /></Field>
          <Field label="Umur Ekonomis (bulan)"><input type="number" value={f.umur_bulan} onChange={e => setF({ ...f, umur_bulan: e.target.value })} className="in" /></Field>
          <Field label="Lokasi Cabang"><input value={f.lokasi_cabang} onChange={e => setF({ ...f, lokasi_cabang: e.target.value })} className="in" /></Field>
          <Field label="PIC"><input value={f.pic} onChange={e => setF({ ...f, pic: e.target.value })} className="in" /></Field>
          <Field label="Dibayar dari Akun" col2><select value={f.sumber_akun_id} onChange={e => setF({ ...f, sumber_akun_id: e.target.value })} className="in"><option value="">- Tidak perlu jurnal pembayaran -</option>{kasBank.map(a => <option key={a.id} value={a.id}>{a.name} · {idr(a.balance)}</option>)}</select></Field>
        </div>
        {susutPerBulan > 0 && <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm">Penyusutan garis lurus: <b>{idr(susutPerBulan)}</b> / bulan selama <b>{f.umur_bulan}</b> bulan.</div>}
        <div className="flex gap-2 mt-5"><button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button><button onClick={submit} disabled={busy || !f.nama || !f.nilai_perolehan || !f.umur_bulan} data-testid="aset-submit" className="flex-1 h-10 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Daftarkan"}</button></div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #4F46E5; }`}</style>
      </div>
    </div>
  );
}

function DetailAset({ id, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/finance/aset-tetap/${id}`).then(r => setD(r.data)); }, [id]);
  // Urutan bawaan (periode asc dari backend) dipertahankan sampai header
  // diklik — log per-aset, dibatasi umur ekonomis aset itu sendiri, bukan
  // potongan sembarang, jadi tidak perlu BatasDaftarNotice.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedHistory = sortRows(d?.history || [], sortKey, sortDir, {
    periode: (h) => h.periode || "",
    penyusutan: (h) => h.penyusutan_bulan || 0,
    buku: (h) => h.nilai_buku_after || 0,
  });
  if (!d) return null;
  const a = d.aset;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] font-mono text-slate-500">{a.code}</p>
            <h3 className="font-display font-bold text-xl">{a.nama}</h3>
            <p className="text-sm text-slate-600 mt-1">{a.kategori} · Lokasi: {a.lokasi_cabang || "-"} · PIC: {a.pic || "-"}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="bg-slate-50 p-3 rounded-lg"><p className="text-[10px] text-slate-500 uppercase">Perolehan</p><p className="font-mono font-bold">{idr(a.nilai_perolehan)}</p></div>
          <div className="bg-indigo-50 p-3 rounded-lg"><p className="text-[10px] text-indigo-700 uppercase">Nilai Buku</p><p className="font-mono font-bold text-indigo-800">{idr(a.nilai_buku)}</p></div>
          <div className="bg-rose-50 p-3 rounded-lg"><p className="text-[10px] text-rose-700 uppercase">Akum Susut</p><p className="font-mono font-bold text-rose-800">{idr(a.akumulasi_penyusutan)}</p></div>
        </div>
        <p className="text-xs text-slate-600 mt-3">Susut/bulan: <b>{idr(a.penyusutan_per_bulan)}</b> · Umur ekonomis: {a.umur_bulan} bulan</p>
        <h4 className="font-display font-bold text-sm mt-5 mb-2">Riwayat Depresiasi</h4>
        {d.history.length === 0 ? <p className="text-xs text-slate-400">Belum ada log depresiasi</p> :
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50"><tr>
                <SortTh label="Periode" sortKeyName="periode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="aset-hist-sort-periode" />
                <SortTh label="Penyusutan" sortKeyName="penyusutan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="aset-hist-sort-penyusutan" />
                <SortTh label="Nilai Buku Setelah" sortKeyName="buku" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="aset-hist-sort-buku" />
              </tr></thead>
              <tbody>{sortedHistory.map(h => (
                <tr key={h.id} className="border-t border-slate-100"><td className="px-3 py-1.5 font-mono">{h.periode}</td><td className="px-3 py-1.5 text-right font-mono text-rose-700">{idr(h.penyusutan_bulan)}</td><td className="px-3 py-1.5 text-right font-mono text-indigo-700">{idr(h.nilai_buku_after)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        }
      </div>
    </div>
  );
}
function Field({ label, children, col2 }) { return <div className={col2 ? "col-span-2" : ""}><label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>{children}</div>; }
